import React, { useState, useEffect } from 'react';
import '../styles/NoticeManagement.css';
import Dialog from '../components/Dialog';
import { 
  getCompanyNotice, 
  updateCompanyNotice, 
  resetCompanyNotice,
  updateCompanyUsersNoticeAgreement
} from '../utils/api';

const NoticeManagement = () => {
  const [language, setLanguage] = useState('한국어');
  const [noticeContent, setNoticeContent] = useState({
    '한국어': '',
    'English': ''
  });
  
  // 원본 공지사항 데이터 (초기화용)
  const [originalNotice, setOriginalNotice] = useState({
    '한국어': '',
    'English': ''
  });
  
  // 다이얼로그 상태 관리
  const [dialog, setDialog] = useState({
    isOpen: false,
    type: '',
  });

  const [notice, setNotice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  useEffect(() => {
    const fetchNotice = async () => {
      try {
        setLoading(true);
        
        // 로컬 스토리지에서 사용자 정보 가져오기
        const userInfo = JSON.parse(localStorage.getItem('userInfo') || '{}');
        const companyCode = userInfo.middleCompanyCode || userInfo.companyCode || '';
        
        const response = await getCompanyNotice(companyCode);
        
        if (response.status === 'success' && response.data) {
          setNotice(response.data);
          
          // 공지사항 내용을 상태에 설정
          const koreanContent = response.data.content || '';
          const englishContent = response.data.content_en || '';
          
          setNoticeContent({
            '한국어': koreanContent,
            'English': englishContent
          });
          
          // 원본 데이터 저장 (초기화용)
          setOriginalNotice({
            '한국어': koreanContent,
            'English': englishContent
          });
        }
      } catch (err) {
        console.error('공지사항 로드 오류:', err);
        setError(err.message || '공지사항을 불러오는데 실패했습니다.');
      } finally {
        setLoading(false);
      }
    };

    fetchNotice();
  }, []);

  const handleLanguageChange = (lang) => {
    setLanguage(lang);
  };

  const handleContentChange = (e) => {
    setNoticeContent({
      ...noticeContent,
      [language]: e.target.value
    });
  };

  // 초기화 버튼 클릭 핸들러
  const handleReset = () => {
    // 초기화 확인 다이얼로그 표시
    setDialog({
      isOpen: true,
      type: 'reset-confirm'
    });
  };

  // 초기화 확인 - API 호출 방식으로 변경
  const confirmReset = async () => {
    try {
      if (!notice || !notice.company_code) {
        throw new Error('회사 정보를 찾을 수 없습니다.');
      }
      
      // API 호출하여 공지사항 초기화
      const response = await resetCompanyNotice(notice.company_code);
      
      // 서버 응답 확인
      if (response.status === 'success' && response.data) {
        console.log('공지사항이 성공적으로 초기화되었습니다.');
        
        // 모든 사용자의 공지사항 동의 상태 초기화 API 호출
        try {
          await updateCompanyUsersNoticeAgreement(notice.company_code, false);
          console.log('모든 사용자의 공지사항 동의 상태가 초기화되었습니다.');
        } catch (userUpdateError) {
          console.error('사용자 공지사항 동의 상태 초기화 오류:', userUpdateError);
          // 사용자 상태 업데이트 실패해도 공지사항 초기화는 계속 진행
        }
        
        // 초기화된 데이터로 상태 업데이트
        const koreanContent = response.data.content || '';
        const englishContent = response.data.content_en || '';
        
        // 내용 업데이트
        setNoticeContent({
          '한국어': koreanContent,
          'English': englishContent
        });
        
        // 원본 데이터도 업데이트
        setOriginalNotice({
          '한국어': koreanContent,
          'English': englishContent
        });
        
        // 초기화 완료 다이얼로그 표시
        setDialog({
          isOpen: true,
          type: 'reset-success'
        });
      } else {
        throw new Error(response.message || '공지사항 초기화에 실패했습니다.');
      }
    } catch (err) {
      console.error('공지사항 초기화 오류:', err);
      
      // 오류 다이얼로그 표시
      setDialog({
        isOpen: true,
        type: 'error',
        message: err.message || '공지사항 초기화 중 오류가 발생했습니다.'
      });
    }
  };

  // 초기화 취소
  const cancelReset = () => {
    setDialog({ isOpen: false, type: '' });
  };

  // 수정완료 버튼 클릭 핸들러
  const handleSubmit = async () => {
    try {
      if (!notice || !notice.company_code) {
        throw new Error('회사 정보를 찾을 수 없습니다.');
      }
      
      // 업데이트할 데이터 준비
      const updateData = {
        content: noticeContent['한국어'],
        content_en: noticeContent['English']
      };
      
      // API 호출하여 공지사항 업데이트
      const response = await updateCompanyNotice(notice.company_code, updateData);
      
      // 서버 응답 확인
      if (response.status === 'success') {
        console.log('공지사항이 성공적으로 업데이트되었습니다.');
        
        // 원본 데이터 갱신
        setOriginalNotice({
          '한국어': noticeContent['한국어'],
          'English': noticeContent['English']
        });
        
        // 수정 완료 다이얼로그 표시
        setDialog({
          isOpen: true,
          type: 'edit-success'
        });
      } else {
        throw new Error(response.message || '공지사항 업데이트에 실패했습니다.');
      }
    } catch (err) {
      console.error('공지사항 업데이트 오류:', err);
      
      // 오류 다이얼로그 표시
      setDialog({
        isOpen: true,
        type: 'error',
        message: err.message || '공지사항 업데이트 중 오류가 발생했습니다.'
      });
    }
  };

  // 다이얼로그 닫기
  const closeDialog = () => {
    setDialog({ isOpen: false, type: '' });
  };

  // 다이얼로그 확인 버튼 핸들러 - 타입에 따라 다른 동작 수행
  const handleDialogConfirm = () => {
    const { type } = dialog;
    
    if (type === 'reset-confirm') {
      // 초기화 확인 다이얼로그의 경우 초기화 실행
      confirmReset();
    } else {
      // 다른 타입(edit-success, reset-success)의 경우 그냥 닫기
      closeDialog();
    }
  };

  if (loading) return <div className="loading">로딩 중...</div>;
  if (error) return <div className="error">{error}</div>;
  
  return (
    <div className="noticeManagementContainer">
      <h1 className="pageTitle" style={{marginBottom: '5px'}}>공지 사항</h1>
      
      <div className="noticeContent" style={{marginTop: '-20px'}}>
        <div className="previewSection">
          <h2 className="sectionTitle" style={{marginBottom: '5px'}}>Preview</h2>
          <div className="previewBox">
            <div className="noticeCard">
              <div className="noticeCardHeader">공지 사항</div>
              <div className="noticeCardBody">
                {noticeContent[language].split('\n').map((line, index) => (
                  <div key={index}>{line || <br />}</div>
                ))}
              </div>
            </div>
          </div>
        </div>
        
        <div className="editSection">
          <h2 className="sectionTitle editTitle" style={{marginBottom: '5px'}}>공지 사항 수정하기</h2>
          <div className="editBox">
            <div className="editHeader">
              <div className="contentAndLanguage">
                <h3>내용</h3>
                <div className="languageSelector">
                  <button 
                    className={language === '한국어' ? 'active' : ''} 
                    onClick={() => handleLanguageChange('한국어')}
                  >
                    한국어
                  </button>
                  <button 
                    className={language === 'English' ? 'active' : ''} 
                    onClick={() => handleLanguageChange('English')}
                  >
                    English
                  </button>
                </div>
              </div>
            </div>
            
            <div className="editContent">
              <textarea 
                value={noticeContent[language]} 
                onChange={handleContentChange}
                placeholder="공지사항 내용을 입력하세요."
                rows={15}
              />
            </div>
            
            <div className="actionButtons">
              <button className="resetButton" onClick={handleReset}>초기화</button>
              <button className="submitButton" onClick={handleSubmit}>수정완료</button>
            </div>
          </div>
        </div>
      </div>
      
      <Dialog 
        isOpen={dialog.isOpen}
        type={dialog.type}
        message={dialog.message}
        onClose={closeDialog}
        onConfirm={handleDialogConfirm}
        onCancel={cancelReset}
      />
    </div>
  );
};

export default NoticeManagement; 